import { apiPath } from '@corp/constants/storage'
import type { Blob } from '@rails/activestorage'
import { readCookie } from '@shared/cookies'

export type BlobWithUrl = Blob & { url: string }

function getBlobUrl ({ signed_id, filename }: Blob) {
  return `${apiPath}/blobs/${signed_id}/${filename}`
}

export function useUploader () {
  const lastBlob = ref<BlobWithUrl>()
  const isUploading = ref(false)
  const progress = ref(0)
  let lastUploadRequest: XMLHttpRequest

  function directUploadWillCreateBlobWithXHR (xhr: XMLHttpRequest) {
    const csrfToken = document.head.querySelector('meta[name="csrf-token"]')
    // is csrf-token is set (phlex pages), we don't want to setRequestHeader here, as it will cause both the
    // csrf-token (rails) and xsrf-token (inertia) to be appended into this header.
    if (!csrfToken)
      xhr.setRequestHeader('X-CSRF-Token', readCookie('CSRF-Token'))
  }

  function directUploadWillStoreFileWithXHR (request: XMLHttpRequest) {
    lastUploadRequest = request
    request.upload.addEventListener('progress', (event) => {
      // Stop uploading files if a new file is being uploaded.
      if (request !== lastUploadRequest)
        return request.abort()

      progress.value = (event.loaded / event.total) * 100
    })
  }

  async function upload (file: File): Promise<BlobWithUrl> {
    isUploading.value = true
    progress.value = 0

    const { DirectUpload } = await import('@rails/activestorage')

    const upload = new DirectUpload(file, `${apiPath}/direct_uploads`, {
      directUploadWillCreateBlobWithXHR,
      directUploadWillStoreFileWithXHR,
    })
    return new Promise((resolve, reject) => {
      upload.create((error, blob) => {
        isUploading.value = false

        if (error)
          reject(error)
        else
          resolve(lastBlob.value = { ...blob, url: getBlobUrl(blob) })
      })
    })
  }

  return {
    lastBlob,
    isUploading,
    progress,
    upload,
  }
}
